/* eslint-disable no-nested-ternary */
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { uuid } from '@carvana/purchase-utils';

const Disabled = css`
  cursor: not-allowed;
  opacity: 0.3 !important;
`;

const Container = styled.div`
  box-sizing: border-box;
  /* margin-bottom: 8px; */
  padding: 15px 8px 0;
  position: relative;
  width: ${props => props.width};

  ${props => props.disabled && Disabled}
`;

const SelectInputWrapper = styled.div`
  position: relative;
  display: inline;

  &:after {
    content: '';
    width: 0;
    height: 500;
    position: absolute;
    pointer-events: none;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    top: 0.3em;
    right: 0.75em;
    border-top: 8px solid #00aed9;
    opacity: 1;

    @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
      opacity: 0;
    }
  }
`;

const SelectInput = styled.select`
  border-color: ${props => props.theme.carvana.grayOne};
  font-size: 0.8rem;
  outline: 0;
  width: 100%;
  background-color: ${props => props.transparent && 'transparent'};
  height: 2.5rem;
  text-overflow: ellipsis;
  color: ${props => props.theme.carvana.grayEighteen};
  white-space: nowrap;
  overflow: hidden;
  appearance: none;
  position: relative;
  padding: 0 0.5rem;
  border-radius: 0;
  font-weight: ${props => (props.bold ? 700 : 500)};

  &:-ms-expand {
    display: none;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
  }

  ${props => props.disabled && Disabled}
`;

const Label = styled.label`
  font-size: 0.8rem;
  position: absolute;
  transform-origin: top left;
  transform: translate(0, 16px) scale(1);
  transition: all 0.3s ease-in-out;
  color: ${props => props.theme.carvana.grayThree};
  color: ${props => props.isFocused && props.theme.carvana.blue};
  color: ${props => props.error && 'red'};

  transform: ${props => props.hasValue && `translate(0, -20px) scale(0.85)`};
  text-align: ${props => props.error && 'left'};

  ${props => props.disabled && Disabled}
`;

const InputTip = styled.div`
  color: ${props => props.theme.carvana.grayFour};
  font-size: 12px;
  font-style: italic;
  font-weight: 700;
  margin-top: 8px;
`;

const InputTipNote = styled.span`
  color: ${props => props.theme.carvana.orange};
`;

class DropdownSelect extends PureComponent {
  inputRef = React.createRef();

  static propTypes = {
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.element
    ]).isRequired,
    onChange: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func,
    onTouched: PropTypes.func,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.string,
    'data-cv-test-override': PropTypes.string,
    hideLabel: PropTypes.bool,
    bold: PropTypes.bool,
    canContainEmptyDefaultValue: PropTypes.bool
  };

  static defaultProps = {
    onTouched: null,
    onKeyDown: null,
    error: false,
    value: 'default',
    width: '100%',
    'data-cv-test-override': null,
    hideLabel: false,
    bold: false,
    canContainEmptyDefaultValue: false
  };

  state = {
    isFocused: false,
    id: uuid()
  };

  onFocus = () => {
    this.setState({ isFocused: true });
  };

  onBlur = () => {
    this.setState({ isFocused: false });
  };

  onChange = e => {
    const { onChange } = this.props;
    onChange(e);
  };

  onKeyDown = e => {
    const { onKeyDown } = this.props;
    if (onKeyDown) {
      return onKeyDown(e);
    }

    if (e.key === 'Escape') {
      this.inputRef.current.blur();
    }

    return e;
  };

  onLabelClick = e => {
    e.preventDefault();
    this.inputRef.current.focus();
  };

  render() {
    const { isFocused, id } = this.state;
    const {
      onTouched,
      'data-cv-test-override': cvDataTestOverride,
      ...rest
    } = this.props;
    const {
      label,
      error,
      value,
      width,
      inputTip,
      disabledStateValue,
      options,
      hideLabel,
      canContainEmptyDefaultValue
    } = rest;
    const hasValue =
      value && value?.toString()?.length > 0 && value !== 'default';

    return (
      <Fragment>
        <Container error={error} width={width} isFocused={isFocused}>
          {(hasValue || canContainEmptyDefaultValue) && !hideLabel && (
            <Label
              onClick={this.onLabelClick}
              hasValue={isFocused || hasValue || canContainEmptyDefaultValue}
              isFocused={isFocused}
              htmlFor={id}
              error={error}
            >
              {error || label}
            </Label>
          )}

          <SelectInputWrapper>
            <SelectInput
              {...rest}
              id={id}
              ref={this.inputRef}
              onClick={this.onFocus}
              onChange={this.onChange}
              onKeyDown={this.onKeyDown}
              onBlur={this.onBlur}
              error={error}
              data-cv-test={cvDataTestOverride || rest['cv-data-test']}
            >
              <option value={disabledStateValue.value} disabled>
                {disabledStateValue.label}
              </option>
              {options.map(x => (
                <option key={x.key} value={x.key}>
                  {x.name}
                </option>
              ))}
            </SelectInput>
          </SelectInputWrapper>
        </Container>

        {inputTip && isFocused && (
          <InputTip>
            <InputTipNote>Note: </InputTipNote>
            {inputTip}
          </InputTip>
        )}
      </Fragment>
    );
  }
}

export default DropdownSelect;
